import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, inject, OnInit, Output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { MatRadioModule } from '@angular/material/radio';
import { IQuestion } from '../../../../core/models/question.model';
import { QUESTION_MANAGEMENT } from '../../../../core/constants/question-solutions';
import { OnlyNumberDirective } from '../../../../shared/directives/only-number.directive';
import { finalize } from 'rxjs';
import { SolutionService } from '../../service/solution.service';
import { CommonService } from '../../../../shared/services/common.service';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { MatDialog } from '@angular/material/dialog';
import {
  investmentFinancial,
  knowledgeMarket,
  objectiveInvestment,
  percentageSavings,
  resultManagement,
  retirementAction,
  retirementDate,
  returnVariation,
} from '../../../../core/constants/question-form-managment';

@Component({
  selector: 'app-form-management',
  templateUrl: './form-management.component.html',
  styleUrls: ['./form-management.component.scss'],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    BaseChartDirective,
    MatRadioModule,
    OnlyNumberDirective,
    NgxCurrencyDirective,
  ],
  standalone: true,
})
export class FormManagementComponent implements OnInit {
  stepIndex: number = 1;
  formManagement: FormGroup = new FormGroup({});
  showResult: boolean = false;
  knowledgeMarketItems = knowledgeMarket;
  objectiveInvestmentItems = objectiveInvestment;
  investmentFinancialItems = investmentFinancial;
  retirementDateItems = retirementDate;
  retirementActionItems = retirementAction;
  returnVariationItems = returnVariation;
  percentageSavingsItems = percentageSavings;
  result: any;
  private solutionService = inject(SolutionService);
  private commonService = inject(CommonService);
  private dialog = inject(MatDialog);
  @Output() changeStepEvent = new EventEmitter<boolean>();
  @Output() hideLogoEvent = new EventEmitter<boolean>();
  public chartType: ChartType = 'pie';
  public chartData: ChartConfiguration['data'] = {
    labels: [
      'Money Market',
      'Renta Fija Core',
      'Renta Fija con Rendimiento Selectivo',
      'Alternativos Estables',
      'Alternativos de Crecimiento',
      'Renta Variable Core',
      'Renta Variable de Alto Potencial',
    ],
    datasets: [
      {
        label: 'Porcentaje: ',
        data: [0, 0, 0, 0, 0, 0, 0],
        backgroundColor: [
          '#688A54',
          '#8A5A54',
          '#546A8A',
          '#484A40',
          '#77548A',
          '#54878A',
          '#FFB9B0',
        ],
      },
    ],
  };
  public chartOptions: ChartConfiguration['options'] = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'right',
        labels: {
          padding: 20,
          boxWidth: 20,
          font: {
            size: 16,
            weight: 'lighter',
            family: 'Montserrat-light',
          },
          color: '#0A3245',
        },
      },
      datalabels: {
        formatter: (value: number) => value + '%',
        color: '#fff',
        font: {
          weight: 'bold',
          family: 'Montserrat-bold',
          size: 14,
        },
      },
    },
  };

  constructor() {}

  ngOnInit() {
    this.setForm();
  }

  ngDoCheck() {
    this.updateLegendPosition();
  }
  
  setForm() {
    this.formManagement = new FormGroup({
      name: new FormControl('', [Validators.required]),
      age: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      country: new FormControl('', [Validators.required]),
      capitalKnowledge: new FormControl('', [Validators.required]),
      objectiveInvestment: new FormControl('', [Validators.required]),
      investmentFinancial: new FormControl('', [Validators.required]),
      retirementDate: new FormControl('', [Validators.required]),
      retirementAction: new FormControl('', [Validators.required]),
      amountInvestment: new FormControl('', [Validators.required]),
      returnVariation: new FormControl('', [Validators.required]),
      percentageSavings: new FormControl('', [Validators.required]),
    });
  }

  backStep() {
    if (this.stepIndex > 1) {
      this.stepIndex--;
    } else if (this.stepIndex === 1) {
      this.changeStepEvent.emit(true);
    }
  }

  nextStep() {
    this.stepIndex++;
    if (this.stepIndex === 4) {
      if (!this.formManagement.valid) {
        return;
      }
      this.saveForm();
    }
  }

  saveForm(contactar: boolean = false): void {
    let form: IQuestion[] = [];
    QUESTION_MANAGEMENT.forEach((item) => {
      if (this.formManagement?.value[item.value]) {
        form.push({
          question: item.question,
          answer: this.formManagement?.value[item.value],
        });
      }
    });
    this.commonService.loading.next(true);
    const payload = {
      tipo: 'Gestión de patrimonio',
      emailClient: this.formManagement?.value.email,
      form: form,
    };
    this.hideLogoEvent.emit(true);
    this.solutionService
      .sendForm(payload)
      .pipe(
        finalize(() => {
          setTimeout(() => {
            this.showResult = true;
            this.commonService.loading.next(false);
          }, 6000);
          if (contactar) {
            this.commonService.loading.next(false);
            this.formManagement.reset();
            this.setForm();
          }
        })
      )
      .subscribe((response) => {
        if (response) {
          if (!contactar) {
            let valueKnowledge = this.knowledgeMarketItems.find(
              (item) =>
                item.label === this.formManagement?.value.capitalKnowledge
            )?.value;
            let valueObjective = this.objectiveInvestmentItems.find(
              (item) =>
                item.label === this.formManagement?.value.objectiveInvestment
            )?.value;
            let valueFinancial = this.investmentFinancialItems.find(
              (item) =>
                item.label === this.formManagement?.value.investmentFinancial
            )?.value;
            let valueRetirementDate = this.retirementDateItems.find(
              (item) => item.label === this.formManagement?.value.retirementDate
            )?.value;
            let valueRetirementAction = this.retirementActionItems.find(
              (item) =>
                item.label === this.formManagement?.value.retirementAction
            )?.value;
            let valueReturnVariation = this.returnVariationItems.find(
              (item) =>
                item.label === this.formManagement?.value.returnVariation
            )?.value;
            let valuePercentageSavings = this.percentageSavingsItems.find(
              (item) =>
                item.label === this.formManagement?.value.percentageSavings
            )?.value;
            let total = valueKnowledge ? valueKnowledge : 0;
            total += valueObjective ? valueObjective : 0;
            total += valueFinancial ? valueFinancial : 0;
            total += valueRetirementDate ? valueRetirementDate : 0;
            total += valueRetirementAction ? valueRetirementAction : 0;
            total += valueReturnVariation ? valueReturnVariation : 0;
            total += valuePercentageSavings ? valuePercentageSavings : 0;
            this.result = resultManagement.find((item) =>
              item.range.includes(total)
            );
            this.chartData.datasets[0].data = this.result.percentages;
          } else {
            let dialogRef = this.dialog.open(AlertComponent, {
              width: '536px',
              data: {
                buttonText: 'Volver a calcular',
              },
            });
            dialogRef.afterClosed().subscribe((result) => {
              this.resetForm();
            });
          }
        }
      },
      (error) => {
        this.commonService.loading.next(false);
      }
    );
  }

  resetForm(): void {
    this.stepIndex = 1;
    this.formManagement.reset();
    this.setForm();
  }

  validateteStep(): boolean {
    let disabled: boolean = false;
    switch (this.stepIndex) {
      case 1:
        disabled =
          !this.formManagement?.get('name')?.valid ||
          !this.formManagement?.get('age')?.valid ||
          !this.formManagement?.get('email')?.valid ||
          !this.formManagement?.get('country')?.valid;
        break;
      case 2:
        disabled =
          !this.formManagement?.get('capitalKnowledge')?.valid ||
          !this.formManagement?.get('objectiveInvestment')?.valid ||
          !this.formManagement?.get('investmentFinancial')?.valid ||
          !this.formManagement?.get('retirementDate')?.valid;
        break;
      case 3:
        disabled =
          !this.formManagement?.get('retirementAction')?.valid ||
          !this.formManagement?.get('amountInvestment')?.valid ||
          !this.formManagement?.get('returnVariation')?.valid ||
          !this.formManagement?.get('percentageSavings')?.valid;
        break;
    }
    return disabled;
  }

  @HostListener('window:resize', [])
  onResize() {
    this.updateLegendPosition();
  }

  updateLegendPosition() {
    const width = window.innerWidth;
    if (width <= 920) {
      this.chartOptions!.plugins!.legend!.position = 'bottom';
      this.chartOptions!.plugins!.legend!.labels! = {
        padding: 10,
        boxWidth: 10,
        font: {
          size: 14,
          weight: 'lighter',
          family: 'Montserrat-light',
        },
        color: '#0A3245',
      }
    } else {
      this.chartOptions!.plugins!.legend!.position = 'right';
    }
  }

  get backgroundColors(): string[] {
    return this.chartData.datasets[0]?.backgroundColor as string[] || [];
  }
}
